// features/consumers/hooks.ts
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/hooks/use-toast";
import { downloadCSV, downloadFile } from "@shared/utils/utils";
import { useNavigate } from "react-router-dom";
import { ServiceOrderFilters } from "../shared/apis/serviceOrderTab";
import {
  CommunicationFilters,
  CommunicationPayload,
} from "../shared/types/communicationTypes";
import { consumerApi } from "./api";
import {
  ComplaintFilters,
  ConsumerFilters,
  CreateConsumerPayload,
  Document,
  DocumentFilters,
  editConsumerPayload,
  FinancialFilters,
  ServiceRequest,
  UpdateConsumerPayload,
} from "./types";
import { formDataToJson } from "@shared/utils/jsonToObjectTransformer";
import { queryClient } from "@shared/api/queries/queryClient";
import { CreatePaymentPayload } from "../payments/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useState } from "react";

// ================================
// CONSUMER PROFILE HOOKS
// ================================

/**
 * Get consumer details
 */
export const useConsumer = (id: any, remoteUtilityId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerDetails(id),
    () => consumerApi.getConsumer(id, remoteUtilityId),
    {
      enabled: !!id,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

/**
 * Update consumer profile
 */

/**
 * Search consumers
 */
export const useSearchConsumers = (
  searchTerm: string,
  remoteUtilityId?: number
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerSearch(
      searchTerm,
      remoteUtilityId
    ),
    () => consumerApi.searchConsumers(searchTerm, remoteUtilityId),
    {
      enabled: searchTerm.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );
};

// ================================
// OVERVIEW TAB HOOKS
// ================================

/**
 * Get consumption data for charts
 */
export const useConsumptionData = (
  id: string,
  period = "6months",
  remoteUtilityId: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerConsumptionData(id, period),
    () => consumerApi.getConsumptionData(id, period, remoteUtilityId),
    {
      enabled: !!id,
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

/**
 * Get relationship insights
 */
export const useRelationshipInsights = (
  id: string,
  remoteUtilityId: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerRelationshipInsights(id),
    () => consumerApi.getRelationshipInsights(id, remoteUtilityId),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// FINANCIAL TAB HOOKS
// ================================

/**
 * Get payment history with filters
 */
export const usePaymentHistory = (
  id: string,
  filters: FinancialFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerPaymentHistory(id, filters),
    () => consumerApi.getPaymentHistory(id, filters),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
};

/**
 * Get billing history with filters
 */
export const useBillingHistory = (
  id: string,
  filters: FinancialFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerBillingHistory(id, filters),
    () => consumerApi.getBillingHistory(id, filters),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
};

// ================================
// CONNECTIONS TAB HOOKS
// ================================

/**
 * Get utility connections
 */
export const useUtilityConnections = (
  consumerNo: string,
  remoteUtilityId: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerUtilityConnections(consumerNo),
    async () => {
      const consumerData = await consumerApi.getConsumer(
        consumerNo,
        remoteUtilityId
      );
      return consumerData.consumerMappingData;
    },
    {
      enabled: !!consumerNo,
      staleTime: 5 * 60 * 1000,
    }
  );
};



export const useConnectionsHistory = (consumerID: string, remoteUtilityId: string) => {
  const key =
    QueryKeyFactory.module.cx.consumers.consumerConnectionsHistory(consumerID);

  return useSmartQuery(key as unknown[], () =>
    consumerApi.getConsumerConnectionHistory(consumerID, remoteUtilityId)
  );
};

/**
 * Get meter readings
 */
export const useMeterReadings = (
  consumerId: string,
  meterId: string,
  filters: { page: number; limit: number },
  remoteUtilityId: number,
  enabled: boolean = true
) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.cx.consumers.consumerMeterReadings(
        consumerId,
        meterId
      ),
      filters,
    ],
    () =>
      consumerApi.getMeterReadings(
        consumerId,
        meterId,
        filters,
        String(remoteUtilityId)
      ),
    {
      enabled: enabled && !!consumerId && !!meterId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// SERVICE TAB HOOKS
// ================================

/**
 * Get service orders with filters
 */
export const useServiceOrders = (
  id: string,
  filters: ServiceOrderFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerServiceOrders(id),
    () => consumerApi.getServiceOrders(id, filters),
    {
      enabled: !!id,
      staleTime: 2 * 60 * 1000,
    }
  );
};

// ================================
// COMPLAINTS TAB HOOKS
// ================================

/**
 * Get consumer complaints with filters
 */
export const useConsumerComplaints = (
  id: string,
  filters: ComplaintFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerComplaints(id),
    () => consumerApi.getComplaints(id, filters),
    {
      enabled: !!id,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Create complaint for consumer
 */
export const useCreateConsumerComplaint = (id: string) => {
  return useSmartMutation(
    ({
      id,
      remoteUtilityId,
      data,
    }: {
      id: string;
      remoteUtilityId: string;
      data: any;
    }) => consumerApi.createComplaint(id, remoteUtilityId, data),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.consumers.consumerComplaints(id),
        QueryKeyFactory.module.cx.consumers.consumerActivity(id),
        QueryKeyFactory.module.cx.complaints.complaintList(), // Global complaints
      ],
      onSuccess: () => {
        toast({
          title: "Complaint registered successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to register complaint",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// SERVICE REQUESTS TAB HOOKS
// ================================

/**
 * Get service requests with filters
 */
export const useServiceRequests = (
  id: string,
  filters: ServiceOrderFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerServiceRequests(id),
    () => consumerApi.getServiceRequests(id, filters),
    {
      enabled: !!id,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Create service request
 */
export const useCreateServiceRequest = (id: string) => {
  return useSmartMutation(
    ({
      id,
      remoteUtilityId,
      data,
    }: {
      id: string;
      remoteUtilityId: string;
      data: Omit<ServiceRequest, "id" | "date">;
    }) => consumerApi.createServiceRequest(id, remoteUtilityId, data),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.consumers.consumerServiceRequests(id),
        QueryKeyFactory.module.cx.consumers.consumerActivity(id),
      ],
      onSuccess: () => {
        toast({
          title: "Service request created successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to create service request",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// COMMUNICATION TAB HOOKS
// ================================

/**
 * Get communication history with filters
 */
export const useCommunicationHistory = (
  id: string,
  filters: CommunicationFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerCommunications(id, filters),
    () => consumerApi.getCommunicationHistory(id, filters),
    {
      enabled: !!id,
      staleTime: 1 * 60 * 1000,
    }
  );
};

/**
 * Send communication
 */
export const useSendCommunication = (id: string) => {
  return useSmartMutation(
    (data: CommunicationPayload) => consumerApi.sendCommunication(data),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.consumers.consumerCommunications(id),
        QueryKeyFactory.module.cx.consumers.consumerActivity(id),
      ],
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["cx", "communication", "communicationHistory", id],
        });
        toast({
          title: "Communication sent successfully!",
          variant: "default",
        });
      },
    }
  );
};

// ================================
// DOCUMENTS TAB HOOKS
// ================================

/**
 * Get documents with filters
 */
export const useConsumerDocuments = (
  id: string,
  filters: DocumentFilters = {}
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerDocuments(id),
    () => consumerApi.getDocuments(id, filters),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Upload document
 */
export const useUploadDocument = (id: string, remoteUtilityId: string) => {
  return useSmartMutation(
    ({
      id,
      file,
      metadata,
    }: {
      id: string;
      file: File;
      metadata: Partial<Document>;
    }) => consumerApi.uploadDocument(id, remoteUtilityId, file, metadata),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.consumers.consumerDocuments(id),
        QueryKeyFactory.module.cx.consumers.consumerActivity(id),
      ],
      onSuccess: () => {
        toast({
          title: "Document uploaded successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to upload document",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Get consumer list
 */
export const useConsumerList = (filters: ConsumerFilters = {}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerList(filters),
    async () => {
      const response = await consumerApi.getConsumerList(filters);
      return response;
    },
    {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    }
  );
};

export const useConsumerExport = (filters: ConsumerFilters = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerExport(filters),
    async () => {
      setIsExporting(true);
      try {
        const blob = await consumerApi.exportConsumerList(filters);

        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export consumers: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get consumer details with bill data
 */
export const useConsumerWithBillData = (
  consumerNo: string,
  remoteUtilityId: string
) => {
  const consumerQuery = useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerDetails(consumerNo),
    () => consumerApi.getConsumer(consumerNo, remoteUtilityId),
    {
      enabled: !!consumerNo && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const billDataQuery = useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerBillData(consumerNo),
    () => consumerApi.getConsumerBillData(consumerNo, remoteUtilityId),
    {
      enabled: !!consumerNo && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const isLoading = consumerQuery.isLoading || billDataQuery.isLoading;
  const error = consumerQuery.error || billDataQuery.error;
  const data =
    consumerQuery.data && billDataQuery.data
      ? { ...consumerQuery.data, billData: billDataQuery.data.billData }
      : undefined;

  return {
    data,
    isLoading,
    error,
    refetch: () => {
      consumerQuery.refetch();
      billDataQuery.refetch();
    },
  };
};

export const useConsumerStatus = (remoteUtilityId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerStatus(), // Include params in query key for caching
    () => consumerApi.getConsumerStatus({ remote_utility_id: remoteUtilityId })
  );
};

export const useConsumerRelation = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.list(), // Unique query key
    () => consumerApi.getConsumerRelation()
  );
};
export const usePlanInfo = (
  params: {
    consumer_id: string;
    remote_utility_id: any;
    is_plan_info: any;
  },
  options: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.planInfo(params),
    () => consumerApi.getPlanInfo(params),
    {
      enabled: options.enabled,
    }
  );
};

export const usePlan = (params: { remoteUtilityId: string }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.plan(params), // Include params in query key for caching
    () => consumerApi.getPlan(params),
    {
      enabled: !!params.remoteUtilityId,
    }
  );
};

export const usePaymentMode = (
  params: {
    remote_utility_id: any;
  },
  p0: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.paymentMode(params), // Include params in query key for caching
    () => consumerApi.getPaymentMode(params),
    {
      enabled: !!params.remote_utility_id && p0.enabled,
    }
  );
};
export const usePremise = (params: {
  remote_utility_id: any;
  config_level: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.list(params), // Include params in query key for caching
    () => consumerApi.getPremise(params),
    {
      enabled: !!params.remote_utility_id,
    }
  );
};

export const useConsumerDetails = (
  params: {
    remote_utility_id: string;
    consumer_id: string;
    is_detail_info: boolean;
    is_document: boolean;
  },
  options: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.consumerDetails(params),
    () => consumerApi.getDetails(params),
    {
      enabled: options.enabled, // ✅ apply condition
      staleTime: 0,
    }
  );
};

export const useConsumerAddress = (
  params: {
    remote_utility_id: any;
    consumer_id: any;
    is_address_info: any;
  },
  p0: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.consumerAddress(params), // Include params in query key for caching
    () => consumerApi.getAddress(params),
    {
      enabled: !!params.remote_utility_id && p0.enabled,
    }
  );
};

export const useKycInfo = (
  params: {
    remote_utility_id: any;
    consumer_id: any;
    is_kyc_info: any;
  },
  p0: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.kycInfo(params), // Include params in query key for caching
    () => consumerApi.getKycInfo(params),
    {
      enabled: !!params.remote_utility_id && p0.enabled,
    }
  );
};

export const useAddressInfo = (
  params: {
    remote_utility_id: any;
    premise: string;
  },
  p0: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.addressInfo(params), // Include params in query key for caching
    () => consumerApi.getAddressInfo(params),
    {
      enabled: !!params.remote_utility_id && p0.enabled,
    }
  );
};

export const usePlanSearch = (params: {
  remoteUtilityId: string;
  searchField: string;
  searchData: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.searchPlan(params), // Include params in query key for caching
    () => consumerApi.searchPlan(params),
    {
      enabled: !!params.remoteUtilityId,
    }
  );
};

// Updated Create Consumer Hook
export const useCreateConsumer = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: CreateConsumerPayload) => consumerApi.createConsumer(payload),
    {
      onSuccess: (data: any) => {
        toast({
          title: "Consumer created successfully!",
          variant: "default",
        });
        // Invalidate consumer list
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "cxonboarding" &&
            query.queryKey[1] === "consumerList",
        });
        navigate(
          `/cx/consumer-management/manual/edit/${data.result.id}#consumer-details`
        );
        // window.location.reload();
      },
      onError: (error: any) => {
        console.log("the error is here man", error);
        // Handle error
        toast({
          title: "Failed to create consumer",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

export const useEditConsumer = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    ({
      consumerId,
      type,
      payload,
    }: {
      type: string;
      consumerId: string;
      payload: any;
    }) => consumerApi.editConsumer(consumerId, payload, type),
    {
      onSuccess: (data: any, variables: any) => {
        toast({
          title: "Consumer updated successfully!",
          variant: "default",
        });
        // Invalidate consumer list
        queryClient.refetchQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
      },
    }
  );
};
// Updated Consumer Operations Hook
export const useUploadDocumentsForConsumer = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: { data: any; consumerId: string }) =>
      consumerApi.uploadDocumentsForConsumer(payload.consumerId, payload.data),
    {
      onSuccess: async (data, variables) => {
        toast({
          title: "Documents uploaded successfully!",
          variant: "default",
        });
        // navigate(
        //   `/cx/consumer-management/manual/edit/${variables.consumerId}#plan-selection`
        // );
        // window.location.reload();
      },
      onError: async (error: any) => {
        toast({
          title: "Failed to upload documents",
          description: error.message,
          variant: "destructive",
        });
      },
    }
  );
};

export const useConsumerOperations = () => {
  const createMutation = useCreateConsumer();
  const editMutation = useEditConsumer();
  const updateMutation = useUpdateConsumer();

  const createConsumer = async (consumerData: CreateConsumerPayload) => {
    return await createMutation.mutateAsync(consumerData);
  };
  const editConsumer = async (
    consumerId: any,
    consumerData: editConsumerPayload
  ) => {
    return await editMutation.mutateAsync({
      consumerId,
      payload: consumerData,
    });
  };

  const updateConsumer = async (
    consumerId: string,
    consumerData: UpdateConsumerPayload
  ) => {
    return await updateMutation.mutateAsync({
      consumerId,
      payload: consumerData,
    });
  };

  return {
    // Create operations
    createConsumer,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    createSuccess: createMutation.isSuccess,

    editConsumer,
    isediting: editMutation.isPending,
    editError: editMutation.error,
    editSuccess: editMutation.isSuccess,
    // Update operations
    updateConsumer,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    updateSuccess: updateMutation.isSuccess,

    // Combined loading state
    isLoading: createMutation.isPending || updateMutation.isPending,
  };
};

export const useUpdateConsumer = () => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: string;
      payload: UpdateConsumerPayload;
    }) => consumerApi.updateConsumer(consumerId, payload)
  );
};

export const useDocumentType = (params: {
  remote_utility_id: any;
  config_level: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.documentType(params), // Include params in query key for caching
    () => consumerApi.getDocumentType(params)
  );
};

export const useDocumentSubtype = (
  params: {
    remote_utility_id: any;
    config_level: string;
    code_list: string;
  },
  p0: { enabled: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.documentSubType(params), // Include params in query key for caching
    () => consumerApi.getDocumentSubtype(params)
  );
};

export const useConsumerSummary = (params: { remote_utility_id: number }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.consumerSummary(
      params.remote_utility_id
    ), // Include params in query key for caching
    () => consumerApi.getConsumerSummary(params),
    {
      enabled: !!params.remote_utility_id,
    }
  );
};

export const useChannelType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.channelType(),
    () => consumerApi.getChannelType(),
    {
      enabled: true,
    }
  );
};

export const useChannelTemplates = (channelType: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.channelTemplates(channelType),
    () => consumerApi.getChannelTemplates(remoteUtilityId, channelType),
    {
      enabled: !!channelType && !!remoteUtilityId,
    }
  );
};

export const useDownloadBill = () => {
  return useSmartMutation(
    async ({
      billId,
      remoteUtilityId,
    }: {
      billId: string;
      remoteUtilityId: number;
    }) => {
      const filename = `bill-${billId}.pdf`;
      const blob = await consumerApi.downloadBill(billId, remoteUtilityId);
      downloadFile(blob, filename);
      return blob;
    }
  );
};

export const usePaymentReceipt = () => {
  return useSmartMutation(
    async ({
      paymentId,
      remoteUtilityId,
    }: {
      paymentId: string;
      remoteUtilityId: number;
    }) => {
      const filename = `receipt-${paymentId}.pdf`;
      const blob = await consumerApi.downloadPaymentReceipt(
        paymentId,
        remoteUtilityId
      );
      downloadFile(blob, filename);
      return blob;
    }
  );
};

export const useCategoryOptions = (remoteUtilityId: number) => {
  const key = QueryKeyFactory.module.cx.consumers.category();
  return useSmartQuery(key, () =>
    consumerApi.getCategories(remoteUtilityId.toString())
  );
};

export const useSubcategoryOptions = (
  remoteUtilityId: number,
  categoryCode: string
) => {
  const key = QueryKeyFactory.module.cx.consumers.subCategory(categoryCode);
  return useSmartQuery(
    key,
    () =>
      consumerApi.getSubcategories(remoteUtilityId.toString(), categoryCode),
    {
      enabled: !!remoteUtilityId && !!categoryCode,
    }
  );
};

export const useEmailVerifyChecker = (
  remoteUtilityId: number,
  email: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.verifyEmail(email, remoteUtilityId),
    () => consumerApi.getEmailVerify(email, remoteUtilityId),
    {
      enabled: !!email && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );
};

/**
 * Get payment received status choices
 */
export const usePaymentChannelChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.paymentChannelChoices(remoteUtilityId),
    () => consumerApi.getPaymentChannelChoices(remoteUtilityId),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get payment pay type choices
 */
export const usePaymentPayTypeChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.paymentPayTypeChoices(remoteUtilityId),
    () => consumerApi.getPaymentPayTypeChoices(remoteUtilityId),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const usePaymentMethod = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.paymentMethod(remoteUtilityId),
    () => consumerApi.getPaymentMethod({ remoteUtilityId }),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Create payment
 */
export const useCreatePayment = (
  remoteUtilityId: number,
  consumer: number,
  consumerNo: string
) => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: CreatePaymentPayload) => consumerApi.createPayment(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.consumers.consumerSearch(
          consumerNo,
          remoteUtilityId
        ),
        QueryKeyFactory.module.cx.consumers.paymentAgreements(remoteUtilityId),
      ],
      onSuccess: (data, variables) => {
        navigate("/cx/payments/success", {
          state: { ...data, formData: variables },
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create payment: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const usePlanDetails = (remoteUtilityId: number, planId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumers.planDetails(remoteUtilityId, planId),
    () =>
      consumerApi.getPlanDetails({
        remoteUtilityId: remoteUtilityId.toString(),
        planId: planId,
      }),
    {
      enabled: !!remoteUtilityId && !!planId,
    }
  );
};
